import {
	BarChartOutlined,
	DownloadOutlined,
	HomeOutlined,
	LeftOutlined,
	ShoppingCartOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Select, Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import { getAdminUsers } from "@/modules/users/services/users.backend";
import { formatMoney } from "@/shared/utils/money";
import type { SalesReportData } from "../services/sales.api";
import { getSalesReport } from "../services/sales.api";

const { RangePicker } = DatePicker;

function formatNum(value: number): string {
	return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function SalesReportPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const fromDashboard = (location.state as any)?.fromDashboard === true;
	const [loading, setLoading] = useState(true);
	const [report, setReport] = useState<SalesReportData | null>(null);
	const [buildingId, setBuildingId] = useState<string | undefined>();
	const [managerId, setManagerId] = useState<string | undefined>();
	const [buildings, setBuildings] = useState<{ id: string; name: string }[]>([]);
	const [managers, setManagers] = useState<{ id: string; name: string }[]>([]);
	const [dateRange, setDateRange] = useState<[string, string] | null>(() => {
		const state = location.state as any;
		if (state?.filter) {
			const labels = [
				"Yan",
				"Fev",
				"Mar",
				"Apr",
				"May",
				"Iyun",
				"Iyul",
				"Avg",
				"Sen",
				"Okt",
				"Noy",
				"Dek",
			];
			const monthIndex = labels.indexOf(state.filter);
			if (monthIndex !== -1) {
				const start = dayjs().month(monthIndex).startOf("month").format("YYYY-MM-DD");
				const end = dayjs().month(monthIndex).endOf("month").format("YYYY-MM-DD");
				return [start, end];
			}

			if (state.filter.includes("hafta")) {
				const weekNum = parseInt(state.filter.split("-")[0], 10);
				if (!Number.isNaN(weekNum)) {
					const start = dayjs()
						.startOf("month")
						.add(weekNum - 1, "week")
						.startOf("week")
						.format("YYYY-MM-DD");
					const end = dayjs()
						.startOf("month")
						.add(weekNum - 1, "week")
						.endOf("week")
						.format("YYYY-MM-DD");
					return [start, end];
				}
			}
		}
		return null;
	});

	useEffect(() => {
		let cancelled = false;
		setLoading(true);

		getSalesReport({
			...(dateRange ? { dateFrom: dateRange[0], dateTo: dateRange[1] } : {}),
			...(buildingId ? { buildingId } : {}),
			...(managerId ? { managerId } : {}),
		})
			.then((data) => {
				if (!cancelled) {
					setReport(data);
					setManagers((prev) =>
						prev.length > 0
							? prev
							: data.byManager.map((m) => ({ id: m.managerId, name: m.fullName }))
					);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [dateRange, buildingId, managerId]);

	useEffect(() => {
		buildingsApi
			.getBuildings()
			.then(setBuildings)
			.catch(() => []);

		getAdminUsers()
			.then((res) => {
				const allUsers = res.items || [];
				const mgrs = allUsers.filter(
					(u: any) => u.role === "manager" || u.role?.toLowerCase().includes("manager")
				);
				if (mgrs.length > 0) {
					setManagers(mgrs.map((m: any) => ({ id: m.id, name: m.fullName })));
				}
			})
			.catch((err) => {
				console.error("Failed to load managers:", err);
			});
	}, []);

	const collectionRate =
		report && report.totalAmount > 0
			? Math.round((report.totalPaid / report.totalAmount) * 100)
			: 0;

	const maxMonthly = report ? Math.max(...report.byMonth.map((item) => item.revenue), 1) : 1;

	const isFirstLoad = loading && !report;

	if (isFirstLoad) {
		return (
			<div
				className="crm-page"
				style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}
			>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="crm-page">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
						{fromDashboard && (
							<Button icon={<LeftOutlined />} onClick={() => navigate("/dashboard")} type="text" />
						)}
						<h1>Sotuv hisoboti</h1>
					</div>
					<p>Sotuvlar bo'yicha umumiy tahlil</p>
				</div>
				<div className="crm-report-actions">
					<RangePicker
						className="crm-report-range"
						value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
						onChange={(_, value) =>
							setDateRange(value[0] && value[1] ? [value[0], value[1]] : null)
						}
					/>
					<Select
						allowClear
						placeholder="Barcha bino"
						style={{ minWidth: 140 }}
						value={buildingId}
						onChange={(v) => setBuildingId(v)}
						options={buildings.map((b) => ({ label: b.name, value: b.id }))}
					/>
					<Select
						allowClear
						placeholder="Barcha menejer"
						style={{ minWidth: 160 }}
						value={managerId}
						onChange={(v) => setManagerId(v)}
						options={managers.map((m) => ({ label: m.name, value: m.id }))}
					/>
					<Button icon={<DownloadOutlined />}>Eksport</Button>
				</div>
			</div>

			<Spin spinning={loading}>
				{report && (
					<>
						<section className="crm-report-kpis">
							<div className="crm-kpi crm-report-kpi">
								<div className="crm-kpi-title">
									<span className="crm-kpi-icon blue">
										<ShoppingCartOutlined />
									</span>
									Jami sotuv
								</div>
								<div className="crm-kpi-value">
									<strong>{formatNum(report.totalSales)}</strong>
									<span>ta</span>
								</div>
								<div className="crm-kpi-note">
									naqd {report.cashSales} - muddatli {report.installmentSales}
								</div>
							</div>

							<div className="crm-kpi crm-report-kpi">
								<div className="crm-kpi-title">
									<span className="crm-kpi-icon green">
										<BarChartOutlined />
									</span>
									Umumiy summa
								</div>
								<div className="crm-kpi-value">
									<strong>{formatMoney(report.totalAmount)}</strong>
								</div>
								<div className="crm-kpi-note">shartnoma qiymati</div>
							</div>

							<div className="crm-kpi crm-report-kpi">
								<div className="crm-kpi-title">
									<span className="crm-kpi-icon sky">
										<WalletOutlined />
									</span>
									O'rtacha narx
								</div>
								<div className="crm-kpi-value">
									<strong>{formatMoney(report.averagePrice)}</strong>
								</div>
								<div className="crm-kpi-note">bitta sotuv uchun</div>
							</div>

							<div className="crm-kpi crm-report-kpi">
								<div className="crm-kpi-title">
									<span className="crm-kpi-icon violet">
										<HomeOutlined />
									</span>
									Yig'ilgan
								</div>
								<div className="crm-kpi-value">
									<strong>{collectionRate}%</strong>
								</div>
								<div className="crm-kpi-note">{formatMoney(report.totalPaid)} qabul qilingan</div>
							</div>
						</section>

						<section className="crm-report-grid">
							<div className="crm-card crm-report-chart-card">
								<div className="crm-card-head">
									<h3>Oylar bo'yicha dinamika</h3>
								</div>
								<div className="crm-report-bars">
									{report.byMonth.map((item) => {
										const height = Math.max(8, (item.revenue / maxMonthly) * 100);
										return (
											<div className="crm-report-bar" key={item.month}>
												<div className="crm-report-bar-track">
													<span style={{ height: `${height}%` }} />
												</div>
												<strong>{item.month}</strong>
												<small>{item.sales}</small>
											</div>
										);
									})}
									{report.byMonth.length === 0 && (
										<div style={{ padding: "40px 0", color: "#999" }}>Ma'lumot mavjud emas</div>
									)}
								</div>
							</div>

							<div className="crm-card crm-table-card crm-report-table-card">
								<div className="crm-card-head">
									<h3>Bino bo'yicha</h3>
								</div>
								<div className="crm-table-wrap">
									{report.byBuilding.length > 0 ? (
										<table className="crm-table">
											<thead>
												<tr>
													<th>Bino</th>
													<th style={{ textAlign: "right" }}>Sotuv</th>
													<th style={{ textAlign: "right" }}>Daromad</th>
												</tr>
											</thead>
											<tbody>
												{report.byBuilding.map((row) => (
													<tr key={row.buildingName}>
														<td>
															<strong>{row.buildingName}</strong>
														</td>
														<td style={{ textAlign: "right" }}>{row.sales} ta</td>
														<td style={{ textAlign: "right" }}>
															<span className="num">{formatMoney(row.revenue)}</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									) : (
										<div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
											Ma'lumot mavjud emas
										</div>
									)}
								</div>
								{report.byBuilding.length > 0 && (
									<div className="crm-table-foot">
										<span>Jami {report.byBuilding.length} ta bino</span>
									</div>
								)}
							</div>
						</section>
					</>
				)}
			</Spin>
		</div>
	);
}

export default SalesReportPage;
